import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Link } from 'react-router-dom';
import CreateTaskModal from './CreateTaskModal';
import EditTaskModal from './EditTaskModal';
import KanbanBoard from '../../../components/KanbanBoard';
import TimelineView from '../../../components/TimelineView';
import ConfirmModal from '../../../components/ConfirmModal';
import UserAvatars from '../../../components/UserAvatars';
import TaskMessages from '../../../components/TaskMessages';
import TaskFilesList from '../../../components/TaskFilesList';

const Tasks = () => {
  const { currentLang, fetchWithAuth, API_URL, getCsrfToken, user } = useAuth();
  
  // Состояние для данных
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Состояние для пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const [limit] = useState(50);
  
  // Состояние для фильтров
  const [filters, setFilters] = useState({
    uid: '', // Убираем фильтр по умолчанию
    site_id: '',
    d_start_from: '',
    d_start_to: '',
    d_end_from: '',
    d_end_to: '',
    status: '',
    name: '',
  });
  
  // Состояние для сортировки
  const [sortField, setSortField] = useState('d_end');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Состояние для отображения (таблица/канбан/таймлайн)
  const [viewMode, setViewMode] = useState('table'); // 'table', 'kanban' или 'timeline'
  
  // Состояние для раскрытия фильтров
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // Флаг первой загрузки
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Функции для работы с localStorage
  const STORAGE_KEY = 'seo-tasks-settings';
  
  const getCurrentDate = () => {
    return new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  };
  
  const saveSettingsToStorage = (settings) => {
    try {
      const settingsWithDate = {
        ...settings,
        savedDate: getCurrentDate()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settingsWithDate));
    } catch (error) {
      // Ошибка сохранения настроек в localStorage
    }
  };
  
  const loadSettingsFromStorage = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const settings = JSON.parse(saved);
        const currentDate = getCurrentDate();
        
        // Проверяем, сохранены ли настройки сегодня
        if (settings.savedDate === currentDate) {
          // Удаляем дату из настроек перед возвратом
          const { savedDate, ...settingsWithoutDate } = settings;
          return settingsWithoutDate;
        } else {
          // Если настройки не сегодняшние, удаляем их
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (error) {
      // Ошибка загрузки настроек из localStorage
    }
    return null;
  };
  
  // Состояние для модального окна создания задачи
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [taskToCopy, setTaskToCopy] = useState(null);
  
  // Состояние для модального окна редактирования задачи
  const [editModal, setEditModal] = useState({ isOpen: false, task: null });
  
  // Состояние для удаления задач
  const [deletingTasks, setDeletingTasks] = useState(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  // Состояние для быстрого изменения статуса
  const [updatingStatus, setUpdatingStatus] = useState(new Set());
  
  // Состояние для открытых дропдаунов статусов
  const [openStatusDropdowns, setOpenStatusDropdowns] = useState(new Set());

  // Состояние для сайтов
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);

  // Состояние для модального окна комментариев
  const [messagesModal, setMessagesModal] = useState({ isOpen: false, taskId: null, task: null });
  
  // Состояние для модального окна файлов
  const [filesModal, setFilesModal] = useState({ isOpen: false, taskId: null });

  // Функции для работы с датами
  const getCurrentMonthRange = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    return {
      from: startOfMonth.toISOString().split('T')[0],
      to: endOfMonth.toISOString().split('T')[0]
    };
  };

  const getLastMonthRange = () => {
    const now = new Date();
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    
    return {
      from: startOfLastMonth.toISOString().split('T')[0],
      to: endOfLastMonth.toISOString().split('T')[0]
    };
  };

  // Загрузка данных
  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Формируем параметры запроса
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: limit.toString(),
        sort_field: sortField,
        sort_direction: sortDirection,
      });
      
      // Добавляем фильтры, если они заполнены
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          // Для строковых значений используем trim, для числовых - преобразуем в строку
          let stringValue;
          if (key === 'site_id' || key === 'uid') {
            stringValue = String(value);
          } else {
            stringValue = typeof value === 'string' ? value.trim() : String(value);
          }
          if (stringValue !== '') {
            params.append(key, stringValue);
          }
        }
      });
      
      const url = `${API_URL}${currentLang}/seo-tasks?${params.toString()}`;
      
      const response = await fetchWithAuth(url);
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setTasks(data.data.tasks || []);
        setTotalPages(data.data.pagination?.pages || 1);
        setTotalTasks(data.data.pagination?.total || 0);
      } else {
        throw new Error(data.message || 'Ошибка загрузки задач');
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
        setError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Инициализация настроек из localStorage
  useEffect(() => {
    const savedSettings = loadSettingsFromStorage();
    
    if (savedSettings) {
      // Загружаем вид отображения
      if (savedSettings.viewMode) {
        setViewMode(savedSettings.viewMode);
      }
      
      // Загружаем состояние раскрытия фильтров
      if (savedSettings.filtersExpanded !== undefined) {
        setFiltersExpanded(savedSettings.filtersExpanded);
      }
      
      // Загружаем фильтры, если они есть
      if (savedSettings.filters) {
        setFilters(prev => ({
          ...prev,
          ...savedSettings.filters
        }));
      }
      
      // Загружаем сортировку
      if (savedSettings.sortField) {
        setSortField(savedSettings.sortField);
      }
      if (savedSettings.sortDirection) {
        setSortDirection(savedSettings.sortDirection);
      }

    } else {
      // Если нет сохраненных настроек, устанавливаем фильтры по умолчанию (текущий месяц)
      const currentMonth = getCurrentMonthRange();
      setFilters(prev => ({
        ...prev,
        uid: '', // Показываем все задачи по умолчанию
        d_start_from: currentMonth.from,
        d_start_to: currentMonth.to
      }));
    }
    
    // Помечаем, что инициализация завершена
    setIsInitialLoad(false);
  }, []);
  
  // Загружаем данные при изменении параметров (но не при первой загрузке)
  useEffect(() => {
    if (!isInitialLoad) {
      fetchTasks();
    }
  }, [currentPage, sortField, sortDirection, filters, isInitialLoad]);
  
  // Первая загрузка данных после инициализации фильтров
  useEffect(() => {
    if (!isInitialLoad) {
      fetchTasks();
    }
  }, [isInitialLoad]);

  // Загружаем сайты при первой загрузке
  useEffect(() => {
    fetchSites();
  }, []);

  // Автоматически раскрываем фильтры при наличии активных фильтров
  useEffect(() => {
    if (getActiveFiltersCount() > 0 && !filtersExpanded) {
      setFiltersExpanded(true);
    }
  }, [filters]);

  const fetchSites = async () => {
    try {
      setSitesLoading(true);
      
      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo/sites`);

      const data = await response.json();

      if (response.ok && data.success) {
        setSites(data.sites || []);
      } else {
        // Ошибка загрузки сайтов
      }
    } catch (error) {
      // Ошибка загрузки сайтов
    } finally {
      setSitesLoading(false);
    }
  };
  
  // Обработчики фильтров
  const handleFilterChange = (field, value) => {
    // Для числовых полей преобразуем в число или пустую строку
    let processedValue = value;
    if (field === 'uid') {
      processedValue = value === '' ? '' : parseInt(value) || '';
    }
    // site_id теперь обрабатывается как строка, так как используется в select
    
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [field]: processedValue
      };
      
      // Сохраняем фильтры в localStorage
      saveSettingsToStorage({
        viewMode,
        filtersExpanded,
        filters: newFilters,
        sortField,
        sortDirection
      });
      
      return newFilters;
    });
    setCurrentPage(1); // Сбрасываем на первую страницу при изменении фильтров
  };
  
  // Обработчик сортировки
  const handleSort = (field) => {
    let newSortField = sortField;
    let newSortDirection = sortDirection;
    
    if (sortField === field) {
      newSortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      newSortField = field;
      newSortDirection = 'asc';
    }
    
    setSortField(newSortField);
    setSortDirection(newSortDirection);
    
    // Сохраняем сортировку в localStorage
    saveSettingsToStorage({
      viewMode,
      filtersExpanded,
      filters,
      sortField: newSortField,
      sortDirection: newSortDirection
    });
    
    setCurrentPage(1);
  };
  
  // Подсчет активных фильтров
  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => {
      if (value === null || value === undefined || value === '') return false;
      if (typeof value === 'string') return value.trim() !== '';
      return true; // Для числовых значений
    }).length;
  };

  // Очистка фильтров
  const clearFilters = () => {
    const currentMonth = getCurrentMonthRange();
    const newFilters = {
      uid: '', // Показываем все задачи по умолчанию
      site_id: '',
      d_start_from: currentMonth.from,
      d_start_to: currentMonth.to,
      d_end_from: '',
      d_end_to: '',
      status: '',
      name: '',
    };
    
    setFilters(newFilters);
    
    // Сохраняем очищенные фильтры в localStorage
    saveSettingsToStorage({
      viewMode,
      filtersExpanded,
      filters: newFilters,
      sortField,
      sortDirection
    });
    
    setCurrentPage(1);
  };
  
  // Форматирование даты
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU');
    } catch (error) {
      return dateString;
    }
  };
  
  // Получение иконки сортировки
  const getSortIcon = (field) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // Получение статуса задачи
  const getStatusText = (status) => {
    switch (status) {
      case 0: return currentLang === 'ru' ? 'Ожидает' : 'Pending';
      case 1: return currentLang === 'ru' ? 'В работе' : 'In Progress';
      case 2: return currentLang === 'ru' ? 'Завершена' : 'Completed';
      case 99: return currentLang === 'ru' ? 'Отменена' : 'Cancelled';
      default: return '-';
    }
  };

  // Очистка HTML тегов для отображения в таблице
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 0: return 'bg-yellow-100 text-yellow-800';
      case 1: return 'bg-blue-100 text-blue-800';
      case 2: return 'bg-green-100 text-green-800';
      case 99: return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Вычисление дней просрочки
  const getDaysOverdue = (endDate) => {
    if (!endDate) return 0;
    const end = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    const diffTime = today - end;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Проверка, является ли задача просроченной
  const isTaskOverdue = (task) => {
    if (task.status === 2 || task.status === 99) return false; // Завершенные и отмененные не считаются просроченными
    if (!task.d_end) return false;
    const endDate = new Date(task.d_end);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    return endDate < today;
  };

  // Обновление статуса задачи
  const handleStatusChange = async (taskId, newStatus) => {
    try {
      setUpdatingStatus(prev => new Set(prev).add(taskId));
      
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo-tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Обновляем задачу в локальном состоянии
        setTasks(prev => prev.map(task => 
          task.id === taskId ? { ...task, status: newStatus } : task
        ));
        showNotification(currentLang === 'ru' ? 'Статус обновлен!' : 'Status updated!', 'success');
        return true;
      } else {
        throw new Error(data.message || 'Ошибка обновления статуса');
      }
    } catch (error) {
      showNotification(error.message, 'error');
      return false;
    } finally {
      setUpdatingStatus(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  };

  // Показать модалку подтверждения удаления
  const showDeleteConfirm = (taskId) => {
    setTaskToDelete(taskId);
    setShowDeleteModal(true);
  };

  // Подтверждение удаления задачи
  const handleDeleteConfirm = async () => {
    if (!taskToDelete) return;

    setDeletingTasks(prev => new Set(prev).add(taskToDelete));

    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo-tasks/${taskToDelete}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        fetchTasks(); // Обновляем список
        showNotification(currentLang === 'ru' ? 'Задача удалена!' : 'Task deleted!', 'success');
      } else {
        throw new Error(data.message || 'Ошибка удаления задачи');
      }
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setDeletingTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskToDelete);
        return newSet;
      });
      setTaskToDelete(null);
    }
  };

  // Закрыть модалку удаления
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setTaskToDelete(null);
  };

  const handleCopyTask = (task) => {
    setTaskToCopy(task);
    setCreateModalOpen(true);
  };

  // Функция показа уведомлений
  const showNotification = (message, type = 'success') => {
    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      info: 'bg-blue-500',
      warning: 'bg-yellow-500'
    };
    
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${colors[type]} text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full`;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    // Анимация появления
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);
    
    // Автоматическое скрытие через 3 секунды
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  };

  // Проверка, является ли текущий пользователь автором задачи
  const isTaskAuthor = (task) => {
    return task.uid === user?.id;
  };

  // Группировка задач по статусу для канбан-доски
  const getTasksByStatus = () => {
    const grouped = {
      0: [], // Ожидает
      1: [], // В работе
      2: [], // Завершена
      99: [], // Отменена
    };
    
    tasks.forEach(task => {
      if (grouped[task.status] !== undefined) {
        grouped[task.status].push(task);
      }
    });
    
    return grouped;
  };

  const isTaskEditor = (task) => {
    if (!task || !task.access_users || !user) return false;
    const accessUser = task.access_users.find(u => u.id === user.id);
    return accessUser && accessUser.type != 'read';
  };

  // Функция для получения названия сайта по ID
  const getSiteName = (siteId) => {
    if (!siteId || !sites.length) return '';
    const site = sites.find(s => s.id === siteId);
    return site ? site.dns : '';
  };

  // Генерация уникального цвета для сайта по его id
  const getSiteColor = (siteId) => {
    if (!siteId) return '#888';
    // Преобразуем id в число (если строка)
    const idNum = typeof siteId === 'number' ? siteId : parseInt(siteId, 10);
    // Простая формула для генерации hue
    const hue = (idNum * 57) % 360;
    return `hsl(${hue}, 70%, 45%)`;
  };

  // Закрыть все дропдауны статусов
  const closeAllStatusDropdowns = () => {
    setOpenStatusDropdowns(new Set());
  };

  // Переключить дропдаун статуса
  const toggleStatusDropdown = (taskId) => {
    setOpenStatusDropdowns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  // Закрыть конкретный дропдаун
  const closeStatusDropdown = (taskId) => {
    setOpenStatusDropdowns(prev => {
      const newSet = new Set(prev);
      newSet.delete(taskId);
      return newSet;
    });
  };

  // Обработчик клика вне дропдауна
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.status-dropdown')) {
        closeAllStatusDropdowns();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Проверка на пустые файлы в модальном окне
  useEffect(() => {
    if (filesModal.isOpen) {
      const checkEmptyFiles = () => {
        const container = document.getElementById('task-files-container');
        if (container) {
          // Ждем немного, чтобы компонент TaskFilesList успел отрендериться
          setTimeout(() => {
            const hasContent = container.children.length > 0 && 
                              container.children[0].children.length > 0;
            
            if (!hasContent) {
              // Если контейнер пустой, добавляем сообщение о пустом состоянии
              container.innerHTML = `
                <div class="text-center py-12">
                  <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h4 class="text-lg font-medium text-gray-900 mb-2">
                    ${currentLang === 'ru' ? 'Файлы не найдены' : 'No files found'}
                  </h4>
                  <p class="text-gray-500">
                    ${currentLang === 'ru' ? 'К этой задаче пока не прикреплено ни одного файла' : 'No files have been attached to this task yet'}
                  </p>
                </div>
              `;
            }
          }, 500); // Ждем 500мс для рендеринга компонента
        }
      };

      checkEmptyFiles();
    }
  }, [filesModal.isOpen, currentLang]);

  // Компонент выпадающего списка статусов
  const StatusDropdown = ({ task }) => {
    const isOpen = openStatusDropdowns.has(task.id);
    const isUpdating = updatingStatus.has(task.id);

    const statusOptions = [
      { value: 0, label: currentLang === 'ru' ? 'Ожидает' : 'Pending', color: 'yellow' },
      { value: 1, label: currentLang === 'ru' ? 'В работе' : 'In Progress', color: 'blue' },
      { value: 2, label: currentLang === 'ru' ? 'Завершена' : 'Completed', color: 'green' },
      { value: 99, label: currentLang === 'ru' ? 'Отменена' : 'Cancelled', color: 'red' }
    ];

    const currentStatus = statusOptions.find(option => option.value === task.status);

    const getStatusColorClasses = (color) => {
      const colorMap = {
        yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200',
        blue: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200',
        green: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200',
        red: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200'
      };
      return colorMap[color] || 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200';
    };

    return (
      <div className="status-dropdown relative">
        <button
          onClick={() => toggleStatusDropdown(task.id)}
          disabled={isUpdating}
          className={`inline-flex items-center px-3 py-1 text-xs font-bold rounded-full border-2 transition-all duration-200 ${
            isUpdating 
              ? 'opacity-50 cursor-not-allowed' 
              : 'cursor-pointer hover:shadow-md hover:scale-105'
          } ${getStatusColorClasses(currentStatus.color)}`}
        >
          {isUpdating ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
              <span>{currentStatus.label}</span>
            </div>
          ) : (
            <>
              <span>{currentStatus.label}</span>
              <svg 
                className={`w-3 h-3 ml-1 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </>
          )}
        </button>

        {isOpen && !isUpdating && (
          <div className="absolute z-50 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 transform -translate-x-1/2 left-1/2 flex flex-col">
            {statusOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  handleStatusChange(task.id, option.value);
                  closeStatusDropdown(task.id);
                }}
                className={`w-full text-left px-4 py-2 text-sm transition-colors duration-150 ${
                  option.value === task.status
                    ? `${getStatusColorClasses(option.color)} font-semibold`
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${
                    option.color === 'yellow' ? 'bg-yellow-400' :
                    option.color === 'blue' ? 'bg-blue-400' :
                    option.color === 'green' ? 'bg-green-400' :
                    option.color === 'red' ? 'bg-red-400' : 'bg-gray-400'
                  }`}></div>
                  <span>{option.label}</span>
                  {option.value === task.status && (
                    <svg className="w-4 h-4 ml-auto text-current" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-2 sm:p-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-4">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className="flex items-center space-x-1 sm:space-x-2">
            <h2 className="text-xl sm:text-2xl font-bold dark:text-white">{currentLang === 'ru' ? 'SEO Задачи' : 'SEO Tasks'}</h2>
            <svg 
              className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              title={currentLang === 'ru' ? 'Настройки сохраняются автоматически (действуют только сегодня)' : 'Settings are saved automatically (valid only today)'}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          {getActiveFiltersCount() > 0 && (
            <span className="px-2 py-1 text-xs font-bold bg-brand-500 text-white rounded-full">
              {getActiveFiltersCount()} {currentLang === 'ru' ? 'фильтр' : 'filter'}
              {getActiveFiltersCount() > 1 && (currentLang === 'ru' ? 'а' : 's')}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          {/* Переключатель режима отображения */}
          <div className="flex bg-gray-200 rounded-lg p-1 text-xs sm:text-sm">
            <button
              onClick={() => {
                setViewMode('table');
                // Сохраняем вид отображения в localStorage
                saveSettingsToStorage({
                  viewMode: 'table',
                  filtersExpanded,
                  filters,
                  sortField,
                  sortDirection
                });
              }}
              className={`px-2 sm:px-3 py-1 rounded-md font-medium transition-colors ${
                viewMode === 'table' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {currentLang === 'ru' ? 'Таблица' : 'Table'}
            </button>
            <button
              onClick={() => {
                setViewMode('kanban');
                // Сохраняем вид отображения в localStorage
                saveSettingsToStorage({
                  viewMode: 'kanban',
                  filtersExpanded,
                  filters,
                  sortField,
                  sortDirection
                });
              }}
              className={`px-2 sm:px-3 py-1 rounded-md font-medium transition-colors ${
                viewMode === 'kanban' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {currentLang === 'ru' ? 'Канбан' : 'Kanban'}
            </button>
            <button
              onClick={() => {
                setViewMode('timeline');
                // Сохраняем вид отображения в localStorage
                saveSettingsToStorage({
                  viewMode: 'timeline',
                  filtersExpanded,
                  filters,
                  sortField,
                  sortDirection
                });
              }}
              className={`px-2 sm:px-3 py-1 rounded-md font-medium transition-colors ${
                viewMode === 'timeline' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {currentLang === 'ru' ? 'Таймлайн' : 'Timeline'}
            </button>
          </div>
          
          <button
            onClick={() => setCreateModalOpen(true)}
            className="px-3 sm:px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center space-x-1 sm:space-x-2 text-sm"
          >
            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">{currentLang === 'ru' ? 'Создать задачу' : 'Create Task'}</span>
            <span className="sm:hidden">{currentLang === 'ru' ? 'Создать' : 'Create'}</span>
          </button>
          
          <button
            onClick={fetchTasks}
            disabled={loading}
            className="px-3 sm:px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-50 text-sm"
          >
            {loading ? (currentLang === 'ru' ? 'Загрузка...' : 'Loading...') : (currentLang === 'ru' ? 'Обновить' : 'Refresh')}
          </button>
          
          <button
            onClick={() => {
              // Сбрасываем все настройки к значениям по умолчанию
              const currentMonth = getCurrentMonthRange();
              const defaultFilters = {
                uid: '',
                site_id: '',
                d_start_from: currentMonth.from,
                d_start_to: currentMonth.to,
                d_end_from: '',
                d_end_to: '',
                status: '',
                name: '',
              };
              
              setViewMode('table');
              setFiltersExpanded(false);
              setFilters(defaultFilters);
              setSortField('d_end');
              setSortDirection('asc');
              setCurrentPage(1);
              
              // Очищаем localStorage
              localStorage.removeItem(STORAGE_KEY);
              
              showNotification(
                currentLang === 'ru' ? 'Настройки сброшены к значениям по умолчанию' : 'Settings reset to default values',
                'info'
              );
            }}
            className="px-3 sm:px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm"
            title={currentLang === 'ru' ? 'Сбросить настройки (действует только сегодня)' : 'Reset settings (valid only today)'}
          >
            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {/* Фильтры */}
      <div className="bg-white rounded-lg shadow-md mb-4 sm:mb-6 dark:bg-navy-700 dark:text-white dark:border-white/10">
        {/* Заголовок фильтров */}
        <div 
          className="p-3 sm:p-4 cursor-pointer hover:bg-gray-50 border-b border-gray-200 dark:bg-navy-700 dark:text-white dark:border-white/10"
          onClick={() => {
            const newFiltersExpanded = !filtersExpanded;
            setFiltersExpanded(newFiltersExpanded);
            // Сохраняем состояние раскрытия фильтров в localStorage
            saveSettingsToStorage({
              viewMode,
              filtersExpanded: newFiltersExpanded,
              filters,
              sortField,
              sortDirection
            });
          }}
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <h3 className="text-base sm:text-lg font-semibold">{currentLang === 'ru' ? 'Фильтры' : 'Filters'}</h3>
              {getActiveFiltersCount() > 0 && (
                <span className="px-2 py-1 text-xs font-bold bg-brand-500 text-white rounded-full">
                  {getActiveFiltersCount()}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {getActiveFiltersCount() > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    clearFilters();
                  }}
                  className="px-2 sm:px-3 py-1 text-xs sm:text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md"
                >
                  {currentLang === 'ru' ? 'Очистить' : 'Clear'}
                </button>
              )}
              <svg 
                className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-500 transition-transform duration-200 ${filtersExpanded ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          
          {/* Быстрые фильтры */}
          <div className="mt-2 sm:mt-3 flex flex-wrap gap-2 sm:gap-2">
            {/* Фильтры по пользователю */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleFilterChange('uid', '');
                }}
                className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                  filters.uid === '' 
                    ? 'bg-brand-500 text-white border-brand-500 dark:bg-brand-500 dark:text-white dark:border-brand-500' 
                    : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200 dark:bg-navy-700 dark:text-white dark:border-white/10'
                }`}
              >
                {currentLang === 'ru' ? 'Все задачи' : 'All tasks'}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleFilterChange('uid', user?.id || '');
                }}
                className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                  filters.uid === (user?.id || '') 
                    ? 'bg-purple-500 text-white border-purple-500' 
                    : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                }`}
              >
                {currentLang === 'ru' ? 'Мои задачи' : 'My tasks'}
              </button>
            </div>
            
            {/* Фильтры по статусу */}
            <div className="flex flex-wrap gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFilterChange('status', '');
              }}
              className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                filters.status === '' 
                  ? 'bg-brand-500 text-white border-brand-500 dark:bg-brand-500 dark:text-white dark:border-brand-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200 dark:bg-navy-700 dark:text-white dark:border-white/10'
              }`}
            >
              {currentLang === 'ru' ? 'Все статусы' : 'All statuses'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFilterChange('status', '0');
              }}
              className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                filters.status === '0' 
                  ? 'bg-yellow-500 text-white border-yellow-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
            >
              {currentLang === 'ru' ? 'Ожидает' : 'Pending'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFilterChange('status', '1');
              }}
              className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                filters.status === '1' 
                  ? 'bg-blue-500 text-white border-blue-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
            >
              {currentLang === 'ru' ? 'В работе' : 'In Progress'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFilterChange('status', '2');
              }}
              className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                filters.status === '2' 
                  ? 'bg-green-500 text-white border-green-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
            >
              {currentLang === 'ru' ? 'Завершена' : 'Completed'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFilterChange('status', '99');
              }}
              className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                filters.status === '99' 
                  ? 'bg-red-500 text-white border-red-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
            >
              {currentLang === 'ru' ? 'Отменена' : 'Cancelled'}
            </button>
            </div>

            {/* Фильтры по датам */}
            <div className="flex flex-wrap gap-1 sm:gap-2 md:border-l md:border-gray-300 pl-0 md:pl-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const currentMonth = getCurrentMonthRange();
                  handleFilterChange('d_start_from', currentMonth.from);
                  handleFilterChange('d_start_to', currentMonth.to);
                }}
                className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                  filters.d_start_from === getCurrentMonthRange().from && filters.d_start_to === getCurrentMonthRange().to
                    ? 'bg-purple-500 text-white border-purple-500' 
                    : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                }`}
              >
                {currentLang === 'ru' ? 'Этот месяц' : 'This month'}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const lastMonth = getLastMonthRange();
                  handleFilterChange('d_start_from', lastMonth.from);
                  handleFilterChange('d_start_to', lastMonth.to);
                }}
                className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                  filters.d_start_from === getLastMonthRange().from && filters.d_start_to === getLastMonthRange().to
                    ? 'bg-purple-500 text-white border-purple-500' 
                    : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                }`}
              >
                {currentLang === 'ru' ? 'Прошлый месяц' : 'Last month'}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleFilterChange('d_start_from', '');
                  handleFilterChange('d_start_to', '');
                }}
                className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                  filters.d_start_from === '' && filters.d_start_to === ''
                    ? 'bg-purple-500 text-white border-purple-500' 
                    : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                }`}
              >
                {currentLang === 'ru' ? 'Все даты' : 'All dates'}
              </button>
            </div>
          </div>

          {/* Быстрые фильтры по сайтам */}
          {sites.length > 0 && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-xs sm:text-sm font-medium text-gray-700">
                  {currentLang === 'ru' ? 'Быстрые фильтры по сайтам:' : 'Quick site filters:'}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFilterChange('site_id', '');
                  }}
                  className={`px-2 sm:px-3 py-1 text-xs rounded-full border transition-colors ${
                    filters.site_id === '' 
                      ? 'bg-brand-500 text-white border-brand-500' 
                      : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                  }`}
                >
                  {currentLang === 'ru' ? 'Все сайты' : 'All sites'}
                </button>
                {sites.map((site) => site.status == 1 && (
                  <button
                    key={site.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFilterChange('site_id', site.id);
                    }}
                    className={`px-2 sm:px-3 py-1 text-xs rounded-full border transition-colors flex items-center space-x-1 ${
                      filters.site_id === site.id
                        ? 'text-white border-current'
                        : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                    }`}
                    style={{
                      backgroundColor: filters.site_id === site.id ? getSiteColor(site.id) : undefined,
                      borderColor: filters.site_id === site.id ? getSiteColor(site.id) : undefined
                    }}
                  >
                    <div
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: filters.site_id === site.id ? 'white' : getSiteColor(site.id) }}
                    ></div>
                    <span className="max-w-[120px] sm:max-w-[150px] truncate">
                      {site.dns}
                    </span>
                    <span className={`text-xs ${filters.site_id === site.id ? 'text-white' : 'text-gray-500'}`}>
                      {site.status === 1 ? '' : '(Н)'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Раскрывающиеся фильтры */}
        {filtersExpanded && (
          <div className="p-3 sm:p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {/* ID пользователя */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'ID пользователя' : 'User ID'}
                </label>
                <input
                  type="number"
                  value={filters.uid}
                  onChange={(e) => handleFilterChange('uid', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                  placeholder={currentLang === 'ru' ? 'Введите ID пользователя' : 'Enter user ID'}
                />
              </div>
              
              {/* Название задачи */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Название задачи' : 'Task name'}
                </label>
                <input
                  type="text"
                  value={filters.name}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                  placeholder={currentLang === 'ru' ? 'Введите название задачи' : 'Enter task name'}
                />
              </div>
              
              {/* Сайт */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Сайт' : 'Site'}
                </label>
                <select
                  value={filters.site_id || ''}
                  onChange={(e) => handleFilterChange('site_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                  disabled={sitesLoading}
                >
                  <option value="">
                    {sitesLoading 
                      ? (currentLang === 'ru' ? 'Загрузка...' : 'Loading...') 
                      : (currentLang === 'ru' ? 'Все сайты' : 'All sites')
                    }
                  </option>
                  {sites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.dns} {site.status === 1 ? '(Активен)' : '(Неактивен)'}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Статус */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Статус' : 'Status'}
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                >
                  <option value="">{currentLang === 'ru' ? 'Все статусы' : 'All statuses'}</option>
                  <option value="0">{currentLang === 'ru' ? 'Ожидает' : 'Pending'}</option>
                  <option value="1">{currentLang === 'ru' ? 'В работе' : 'In Progress'}</option>
                  <option value="2">{currentLang === 'ru' ? 'Завершена' : 'Completed'}</option>
                  <option value="99">{currentLang === 'ru' ? 'Отменена' : 'Cancelled'}</option>
                </select>
              </div>
              
              {/* Дата начала с */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Дата начала с' : 'Start date from'}
                </label>
                <input
                  type="date"
                  value={filters.d_start_from}
                  onChange={(e) => handleFilterChange('d_start_from', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                />
              </div>
              
              {/* Дата начала по */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Дата начала по' : 'Start date to'}
                </label>
                <input
                  type="date"
                  value={filters.d_start_to}
                  onChange={(e) => handleFilterChange('d_start_to', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                />
              </div>
              
              {/* Дата окончания с */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Дата окончания с' : 'End date from'}
                </label>
                <input
                  type="date"
                  value={filters.d_end_from}
                  onChange={(e) => handleFilterChange('d_end_from', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                />
              </div>
              
              {/* Дата окончания по */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Дата окончания по' : 'End date to'}
                </label>
                <input
                  type="date"
                  value={filters.d_end_to}
                  onChange={(e) => handleFilterChange('d_end_to', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Статистика */}
      <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 mb-4 sm:mb-6 dark:bg-navy-700 dark:text-white dark:border-white/10">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <span className="text-xs sm:text-sm text-gray-600">
              {currentLang === 'ru' ? 'Всего задач:' : 'Total tasks:'} {totalTasks}
            </span>
            {(() => {
              const overdueCount = tasks.filter(task => isTaskOverdue(task)).length;
              return overdueCount > 0 ? (
                <span className="text-xs sm:text-sm text-red-600 font-medium flex items-center space-x-1">
                  <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{currentLang === 'ru' ? 'Просрочено:' : 'Overdue:'} {overdueCount}</span>
                </span>
              ) : null;
            })()}
          </div>
          <div>
            <span className="text-xs sm:text-sm text-gray-600">
              {currentLang === 'ru' ? 'Страница' : 'Page'} {currentPage} {currentLang === 'ru' ? 'из' : 'of'} {totalPages}
            </span>
          </div>
        </div>
      </div>

      {/* Ошибка */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Контент */}
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
        </div>
      ) : viewMode === 'table' ? (
        /* Табличное отображение */
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 dark:bg-navy-700 dark:text-white dark:border-white/10">
                <tr>
                  <th 
                    className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('name')}
                  >
                    <span className="hidden sm:inline">{currentLang === 'ru' ? 'Название' : 'Name'}</span>
                    <span className="sm:hidden">{currentLang === 'ru' ? 'Назв.' : 'Name'}</span>
                    {getSortIcon('name')}
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                    </svg>
                  </th>
                  <th 
                    className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('status')}
                  >
                    <span className="hidden sm:inline">{currentLang === 'ru' ? 'Статус' : 'Status'}</span>
                    <span className="sm:hidden">{currentLang === 'ru' ? 'Стат.' : 'Status'}</span>
                    {getSortIcon('status')}
                  </th>
                  <th 
                    className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('pct')}
                  >
                    <span className="hidden sm:inline">{currentLang === 'ru' ? 'Прогресс' : 'Progress'}</span>
                    <span className="sm:hidden">{currentLang === 'ru' ? 'Прог.' : 'Prog.'}</span>
                    {getSortIcon('pct')}
                  </th>
                  <th 
                    className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('d_start')}
                  >
                    <span className="hidden sm:inline">{currentLang === 'ru' ? 'Дата начала' : 'Start Date'}</span>
                    <span className="sm:hidden">{currentLang === 'ru' ? 'Начало' : 'Start'}</span>
                    {getSortIcon('d_start')}
                  </th>
                  <th 
                    className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('d_end')}
                  >
                    <span className="hidden sm:inline">{currentLang === 'ru' ? 'Дата окончания' : 'End Date'}</span>
                    <span className="sm:hidden">{currentLang === 'ru' ? 'Окончание' : 'End'}</span>
                    {getSortIcon('d_end')}
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="hidden sm:inline">{currentLang === 'ru' ? 'Пользователи' : 'Users'}</span>
                    <span className="sm:hidden">{currentLang === 'ru' ? 'Польз.' : 'Users'}</span>
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="hidden sm:inline">{currentLang === 'ru' ? 'Действия' : 'Actions'}</span>
                    <span className="sm:hidden">{currentLang === 'ru' ? 'Дейст.' : 'Actions'}</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 dark:bg-navy-700 dark:text-white dark:border-white/10">
                {tasks.map((task) => {
                  const isOverdue = isTaskOverdue(task);
                  const daysOverdue = isOverdue ? getDaysOverdue(task.d_end) : 0;
                  
                  return (
                    <tr key={task.id} className={`hover:bg-gray-50 dark:bg-navy-700 dark:text-white dark:border-white/10 ${
                      isOverdue ? 'bg-red-50 hover:bg-red-100 border-l-4 border-red-500' : ''
                    }`}>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-sm text-gray-900 max-w-xs">
                      <div className="flex items-start space-x-1 sm:space-x-2">
                      <Link 
                        to={`/seo/tasks/${task.id}`}
                          className={`${isOverdue ? 'text-red-600 hover:text-red-800' : 'text-blue-600 hover:text-blue-800'} font-medium break-words text-xs sm:text-sm`}
                        title={task.name}
                      >
                        {task.name}
                      </Link>
                        {isOverdue && (
                          <span className="px-1 sm:px-2 py-1 text-xs font-bold rounded-full bg-red-100 text-red-800 flex-shrink-0">
                            {daysOverdue} {currentLang === 'ru' ? 'дн.' : 'days'}
                          </span>
                        )}
                      </div>
                      <div className="text-xs mt-1" title={getSiteName(task.site_id)}>
                        <span
                          className="font-medium break-words flex items-center"
                          style={{ color: getSiteColor(task.site_id) }}
                        >
                          <div
                            className="w-2 h-2 rounded-full mr-1 flex-shrink-0"
                            style={{ backgroundColor: getSiteColor(task.site_id) }}
                          ></div>
                          <span className="hidden sm:inline">{getSiteName(task.site_id)}</span>
                          <span className="sm:hidden">{getSiteName(task.site_id).length > 15 ? getSiteName(task.site_id).substring(0, 15) + '...' : getSiteName(task.site_id)}</span>
                        </span>
                      </div>
                        {task.text && (
                          <div className="text-xs text-gray-500 mt-1 break-words hidden sm:block max-h-[3rem] overflow-hidden" title={stripHtml(task.text)}>
                            {stripHtml(task.text)}
                          </div>
                        )}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center space-x-1">
                      <button
                        onClick={() => setMessagesModal({ isOpen: true, taskId: task.id, task: task })}
                        className="p-1 sm:p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
                        title={currentLang === 'ru' ? 'Комментарии' : 'Comments'}
                      >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                        </svg>
                      </button>
                        <button
                          onClick={() => setFilesModal({ isOpen: true, taskId: task.id })}
                          className="p-1 sm:p-2 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-full transition-colors"
                          title={currentLang === 'ru' ? 'Файлы' : 'Files'}
                        >
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-900">
                      {isTaskEditor(task) ? (
                        <StatusDropdown task={task} />
                      ) : (
                        <span className={`px-1 sm:px-2 py-1 text-xs font-bold rounded-full ${getStatusColor(task.status)}`}>
                          <span className="hidden sm:inline">{getStatusText(task.status)}</span>
                          <span className="sm:hidden">
                            {task.status === 0 ? (currentLang === 'ru' ? 'Ожид.' : 'Pend.') :
                             task.status === 1 ? (currentLang === 'ru' ? 'В раб.' : 'In Prog.') :
                             task.status === 2 ? (currentLang === 'ru' ? 'Завер.' : 'Comp.') :
                             task.status === 99 ? (currentLang === 'ru' ? 'Отмен.' : 'Canc.') : '-'}
                          </span>
                        </span>
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <div className="w-12 sm:w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${task.pct}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-600">{task.pct}%</span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="hidden sm:inline">{formatDate(task.d_start)}</span>
                      <span className="sm:hidden">{formatDate(task.d_start).replace(/\d{4}/, '')}</span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className={`${isOverdue ? 'text-red-600 font-medium' : ''}`}>
                        <span className="hidden sm:inline">{formatDate(task.d_end)}</span>
                        <span className="sm:hidden">{formatDate(task.d_end).replace(/\d{4}/, '')}</span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-900">
                      <UserAvatars 
                        users={task.access_users || []} 
                        maxVisible={3} 
                        size="sm" 
                      />
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex space-x-1 sm:space-x-2">
                        <button
                          onClick={() => handleCopyTask(task)}
                          className="p-1 sm:p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md"
                          title={currentLang === 'ru' ? 'Копировать задачу' : 'Copy task'}
                        >
                          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                        {isTaskEditor(task) && (
                          <>
                            <button
                              onClick={() => setEditModal({ isOpen: true, task })}
                              className="p-1 sm:p-2 text-brand-500 hover:text-brand-700 hover:bg-brand-50 rounded-md"
                              title={currentLang === 'ru' ? 'Редактировать задачу' : 'Edit task'}
                            >
                              <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => showDeleteConfirm(task.id)}
                              disabled={deletingTasks.has(task.id)}
                              className="p-1 sm:p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md disabled:opacity-50"
                              title={currentLang === 'ru' ? 'Удалить' : 'Delete'}
                            >
                              {deletingTasks.has(task.id) ? (
                                <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-red-600"></div>
                              ) : (
                                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              </tbody>
            </table>
          </div>
        </div>
      ) : viewMode === 'kanban' ? (
        /* Канбан-доска с drag & drop */
        <div>
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2 text-blue-700">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm">
                {currentLang === 'ru' 
                  ? '💡 Перетащите задачи между колонками для изменения статуса' 
                  : '💡 Drag tasks between columns to change status'
                }
              </span>
            </div>
          </div>
          <KanbanBoard
            tasks={tasks}
            onDelete={showDeleteConfirm}
            onCopy={handleCopyTask}
            onEdit={(task) => setEditModal({ isOpen: true, task })}
            onStatusChange={handleStatusChange}
            currentLang={currentLang}
            deletingTasks={deletingTasks}
            getStatusText={getStatusText}
            getStatusColor={getStatusColor}
            isTaskEditor={isTaskEditor}
            getSiteName={getSiteName}
            isTaskOverdue={isTaskOverdue}
            getDaysOverdue={getDaysOverdue}
            onMessagesClick={(taskId) => setMessagesModal({ isOpen: true, taskId, task: null })}
            onFilesClick={(taskId) => setFilesModal({ isOpen: true, taskId })}
          />
        </div>
      ) : (
        /* Таймлайн вид */
        <TimelineView
          tasks={tasks}
          currentLang={currentLang}
          onDelete={showDeleteConfirm}
          onCopy={handleCopyTask}
          onEdit={(task) => setEditModal({ isOpen: true, task })}
          onStatusChange={handleStatusChange}
          deletingTasks={deletingTasks}
          getStatusText={getStatusText}
          getStatusColor={getStatusColor}
          isTaskEditor={isTaskEditor}
          getSiteName={getSiteName}
          isTaskOverdue={isTaskOverdue}
          getDaysOverdue={getDaysOverdue}
          onMessagesClick={(taskId) => setMessagesModal({ isOpen: true, taskId, task: null })}
          onFilesClick={(taskId) => setFilesModal({ isOpen: true, taskId })}
          getSiteColor={getSiteColor}
        />
      )}
      
      {/* Пагинация */}
      {totalPages > 1 && (
        <div className="bg-white px-3 sm:px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 sm:mt-6 rounded-lg shadow-md">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              {currentLang === 'ru' ? 'Предыдущая' : 'Previous'}
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="ml-2 sm:ml-3 relative inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              {currentLang === 'ru' ? 'Следующая' : 'Next'}
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                {currentLang === 'ru' ? 'Показано' : 'Showing'} <span className="font-medium">{(currentPage - 1) * limit + 1}</span> {currentLang === 'ru' ? 'до' : 'to'} <span className="font-medium">{Math.min(currentPage * limit, totalTasks)}</span> {currentLang === 'ru' ? 'из' : 'of'} <span className="font-medium">{totalTasks}</span> {currentLang === 'ru' ? 'результатов' : 'results'}
              </p>
            </div>
    <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span className="sr-only">{currentLang === 'ru' ? 'Предыдущая' : 'Previous'}</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === currentPage
                          ? 'z-10 bg-brand-50 border-brand-500 text-brand-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span className="sr-only">{currentLang === 'ru' ? 'Следующая' : 'Next'}</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
      
      {/* Модалка создания задачи */}
      <CreateTaskModal
        isOpen={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setTaskToCopy(null);
        }}
        onSuccess={fetchTasks}
        taskToCopy={taskToCopy}
      />

      {/* Модалка редактирования задачи */}
      <EditTaskModal
        isOpen={editModal.isOpen}
        task={editModal.task}
        onClose={() => setEditModal({ isOpen: false, task: null })}
        onUpdate={fetchTasks}
      />

      {/* Модалка подтверждения удаления */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        title={currentLang === 'ru' ? 'Подтверждение удаления' : 'Confirm Deletion'}
        message={currentLang === 'ru' 
          ? 'Вы уверены, что хотите удалить эту задачу? Это действие нельзя отменить.' 
          : 'Are you sure you want to delete this task? This action cannot be undone.'
        }
        confirmText={currentLang === 'ru' ? 'Удалить' : 'Delete'}
        cancelText={currentLang === 'ru' ? 'Отмена' : 'Cancel'}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        currentLang={currentLang}
      />

      {/* Модалка комментариев */}
      {messagesModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={() => setMessagesModal({ isOpen: false, taskId: null })}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 z-10"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="p-6">
              <TaskMessages taskId={messagesModal.taskId} isTaskEditor={isTaskEditor(messagesModal.task)} />
            </div>
          </div>
        </div>
      )}

      {/* Модалка файлов */}
      {filesModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={() => setFilesModal({ isOpen: false, taskId: null })}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 z-10"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {currentLang === 'ru' ? 'Файлы задачи' : 'Task Files'}
                </h3>
              </div>
              <div id="task-files-container">
                <TaskFilesList taskId={filesModal.taskId} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tasks;
